import { create } from "zustand";
import * as Types from "@/models/types";
import { MOBILE_MAX_WIDTH } from "@/constants";
import {
  CustomerInfo,
  PurchasesPackage,
} from "@revenuecat/purchases-capacitor";
import { ms } from "@/ui/moderateScale";

type State = {
  revenueCat?: {
    packages: PurchasesPackage[];
    customerInfo: CustomerInfo;
  };
  desktopSidebarOpen: boolean;
  footerHeight: number;
  currentAIModel: Types.AIModel;
  searchEnabled?: boolean;
  streamText?: string;
  device: {
    isMobile: boolean;
    width: number;
    height: number;
    safeArea: {
      top: number;
      bottom: number;
    };
  };
  alertWidget?: "selectAIModel";
  alertSelect?: {
    title?: string;
    buttonText?: string;
    defaultOption?: number;
    options: string[];
    onSelect?: (index: number) => void;
    onSelectAsync?: (index: number) => Promise<void>;
  };
  useUploadParams?: {
    fileType: string;
    key: string;
  };
  paywallVisible?: boolean;
  shouldShowPaywall: boolean;
  alertPromptSignIn?: {
    title: string;
    subtitle: string;
    placeholder: string;
    shouldPersistBackdropClick?: boolean;
    multiLine?: boolean;
    loading?: boolean;
    hideDismissIcon?: boolean;
    hideCancel?: boolean;
    defaultValue?: string;
    button: {
      text: string;
      onPress: (text: string) => void;
      destructive?: boolean;
    };
    onGoogleSignIn?: () => void;
  };
  alertModal?: {
    title: string;
    loading?: boolean;
    subtitle: string;
    shouldPersistBackdropClick?: boolean;
    hideDismissIcon?: boolean;
    hideCancel?: boolean;
    onDismiss?: () => void;
    onLeftButtonPress?: () => void;
    button?: { text: string; onPress: () => void; destructive?: boolean };
    cancelText?: string;
  };
  alertPrompt?: {
    title: string;
    subtitle?: string;
    placeholder: string;
    shouldPersistBackdropClick?: boolean;
    multiLine?: boolean;
    loading?: boolean;
    hideDismissIcon?: boolean;
    hideCancel?: boolean;
    defaultValue?: string;
    button: {
      text: string;
      onPress: (text: string) => void;
      destructive?: boolean;
    };
  };
  alertWidgetStartUpload?: {
    file: File;
    language?: string;
    onSuccess?: (params: { downloadUrl: string; language: string }) => void;
  };
};

type Actions = {
  setCurrentAIModel: (payload: State["currentAIModel"]) => void;
  setDevice: (payload: State["device"]) => void;
  setDesktopSidebarOpen: (payload: State["desktopSidebarOpen"]) => void;
  setAlertPromptSignIn: (payload: State["alertPromptSignIn"]) => void;
  setAlertModal: (payload: State["alertModal"]) => void;
  setAlertSelect: (payload: State["alertSelect"]) => void;
  setFooterHeight: (payload: "none" | string) => void;
  setSearchEnabled: (payload: State["searchEnabled"]) => void;
  setAlertWidgetStartUpload: (payload: State["alertWidgetStartUpload"]) => void;
  setUseUploadParams: (payload: State["useUploadParams"]) => void;
  setAlertPrompt: (payload: State["alertPrompt"]) => void;
  setAlertWidget: (payload: State["alertWidget"]) => void;
  setPaywallVisible: (payload: State["paywallVisible"]) => void;
  setRevenueCat: (payload: State["revenueCat"]) => void;
  setStreamText: (payload: State["streamText"]) => void;
  setShouldShowPaywall: (payload: State["shouldShowPaywall"]) => void;
  reset: () => void;
};

// define the initial state
const initialState: State = {
  footerHeight: ms(150),
  searchEnabled: false,
  shouldShowPaywall: true,
  currentAIModel: {
    id: "google/gemini-2.5-flash-lite-preview-06-17",
    name: "Gemini",
    model: "2.5 lite",
    icon: "gemini",
    searchModel: "deepseek/deepseek-chat-v3-0324:online",
    isDefault: true,
    isPremium: false,
    withImage: true,
    withPDF: true,
  },
  desktopSidebarOpen: true,
  device: {
    isMobile: window.innerWidth < MOBILE_MAX_WIDTH,
    width: window.innerWidth,
    height: window.innerHeight,
    safeArea: {
      top: 0,
      bottom: 0,
    },
  },
};

export const useUISlice = create<State & Actions>()((set, get) => ({
  ...initialState,
  setCurrentAIModel: (payload) => {
    set({ currentAIModel: payload });
  },
  setDevice: (payload) => {
    set({ device: payload });
  },
  setDesktopSidebarOpen: (payload) => {
    set({ desktopSidebarOpen: payload });
  },
  setRevenueCat: (payload) => {
    set({ revenueCat: payload });
  },
  setFooterHeight: (payload) => {
    switch (payload) {
      case "none":
        set({ footerHeight: ms(150) });
        break;
      default:
        set({ footerHeight: ms(150) + ms(90) });
        break;
    }
  },
  setAlertSelect: (payload) => {
    set({ alertSelect: payload });
  },
  setAlertWidget: (payload) => {
    set({ alertWidget: payload });
  },
  setAlertPromptSignIn: (payload) => {
    set({ alertPromptSignIn: payload });
  },
  setSearchEnabled: (payload) => {
    set({ searchEnabled: payload });
  },
  setAlertWidgetStartUpload: (payload) => {
    set({ alertWidgetStartUpload: payload });
  },
  setUseUploadParams: (payload) => {
    set({ useUploadParams: payload });
  },
  setAlertModal: (payload) => {
    set({ alertModal: payload });
  },
  setAlertPrompt: (payload) => {
    set({ alertPrompt: payload });
  },
  setPaywallVisible: (payload) => {
    set({ paywallVisible: payload });
  },
  setStreamText: (payload) => {
    set({ streamText: payload });
  },
  setShouldShowPaywall: (payload) => {
    set({ shouldShowPaywall: payload });
  },
  reset: () => {
    set({ ...initialState });
  },
}));
